import React, { useState, useEffect } from "react";
import { api, fmtCurrency, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, CreditCard, Clock, LogOut } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Razorpay. Check your connection."));
    document.body.appendChild(s);
  });
}

export function TrialExpired() {
  const { user, refresh, logout } = useAuth();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const isAdmin = user?.role === "company_admin" || user?.role === "super_admin";

  useEffect(() => {
    api.get("/subscription").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  }, []);

  const payNow = async (planId) => {
    try {
      const { data } = await api.post("/payments/razorpay/order", { plan_id: planId, billing_cycle: cycle });
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: data.company_name, description: `${planId.replace("plan_", "").toUpperCase()} ${cycle} subscription`,
        prefill: { name: data.name, email: data.email, contact: data.contact },
        theme: { color: "#0284c7" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful — your plan is now active!");
            await refresh();
          } catch (e) { toast.error(errMsg(e)); }
        },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed. Please try again."));
      rzp.open();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="trial-expired-screen" className="min-h-full flex items-center justify-center py-8">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-8">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
            <Clock className="h-7 w-7 text-amber-600" />
          </div>
          <h1 className="text-3xl font-bold font-display text-slate-900">Your free trial has ended</h1>
          <p className="text-slate-500 mt-2 max-w-lg mx-auto">
            Your 8-day free trial is over. {isAdmin
              ? "Choose a plan below to keep converting international enquiries into orders."
              : "Ask your company admin to pick a plan to restore access."}
          </p>
        </div>

        {isAdmin ? (
          <>
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-100 p-1" data-testid="expired-cycle-toggle">
                <button onClick={() => setCycle("monthly")} data-testid="expired-cycle-monthly"
                  className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${cycle === "monthly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Monthly</button>
                <button onClick={() => setCycle("annual")} data-testid="expired-cycle-annual"
                  className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${cycle === "annual" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                  Annual <span className="text-emerald-600">· 2 months free</span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {plans.map((p) => (
                <Card key={p.id} className="border border-slate-200/80 rounded-xl shadow-sm p-6 bg-white" data-testid={`expired-plan-${p.id}`}>
                  <p className="font-bold font-display text-lg">{p.name}</p>
                  {cycle === "annual" ? (
                    <div className="mt-3">
                      <p className="text-3xl font-bold font-display">{fmtCurrency(p.price * 10)}<span className="text-sm font-normal text-slate-400">/yr</span></p>
                      <p className="text-xs text-emerald-600 font-medium mt-1">Save {fmtCurrency(p.price * 2)} vs monthly</p>
                    </div>
                  ) : (
                    <p className="text-3xl font-bold font-display mt-3">{fmtCurrency(p.price)}<span className="text-sm font-normal text-slate-400">/mo</span></p>
                  )}
                  <ul className="mt-4 space-y-2">
                    {(p.features || []).map((feat) => (
                      <li key={feat} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />{feat}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => payNow(p.id)} className="w-full mt-5 gap-2 bg-slate-900 hover:bg-slate-800 text-white" data-testid={`expired-pay-${p.id}`}>
                    <CreditCard className="h-4 w-4" /> Pay {cycle === "annual" ? "yearly" : "monthly"} with Razorpay
                  </Button>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <Card className="border border-slate-200/80 rounded-xl shadow-sm p-8 text-center bg-white">
            <p className="text-slate-600">Your free trial has expired. Please contact your company administrator to activate a subscription.</p>
          </Card>
        )}

        <div className="text-center mt-8">
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-rose-600" data-testid="expired-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
